import { useCallback, useEffect, useState } from "react";

/** Evento não-padronizado do Chrome/Android para instalação do PWA. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const CHAVE_DISPENSA = "caint-install-dispensado";
const REAPARECER_MS = 14 * 24 * 60 * 60 * 1000; // 14 dias

function jaInstalado(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  // iOS Safari expõe navigator.standalone (fora do padrão).
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return Boolean(standalone) || iosStandalone;
}

function ehIOS(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent;
  const iDevice = /iphone|ipad|ipod/i.test(ua);
  // iPadOS recente se identifica como Mac com toque.
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iDevice || iPadOS;
}

function dispensadoRecentemente(): boolean {
  try {
    const v = localStorage.getItem(CHAVE_DISPENSA);
    return v !== null && Date.now() - Number(v) < REAPARECER_MS;
  } catch {
    return false;
  }
}

/**
 * Convite para instalar o app no celular. No Android/Chrome usa o evento nativo
 * `beforeinstallprompt` (botão "Instalar"); no iOS, onde esse evento não existe,
 * mostra as instruções de "Adicionar à Tela de Início". Some quando já instalado
 * (display-mode: standalone) ou após o usuário dispensar.
 */
export function InstallPrompt() {
  const [evento, setEvento] = useState<BeforeInstallPromptEvent | null>(null);
  const [mostrarIOS, setMostrarIOS] = useState(false);
  const [oculto, setOculto] = useState(() => jaInstalado() || dispensadoRecentemente());

  useEffect(() => {
    if (oculto) {
      return;
    }
    const aoPrompt = (e: Event) => {
      e.preventDefault();
      setEvento(e as BeforeInstallPromptEvent);
    };
    const aoInstalar = () => {
      setEvento(null);
      setMostrarIOS(false);
      setOculto(true);
    };
    window.addEventListener("beforeinstallprompt", aoPrompt);
    window.addEventListener("appinstalled", aoInstalar);

    // iOS não dispara beforeinstallprompt: decide pelas instruções manuais.
    if (ehIOS() && !jaInstalado()) {
      setMostrarIOS(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", aoPrompt);
      window.removeEventListener("appinstalled", aoInstalar);
    };
  }, [oculto]);

  const instalar = useCallback(async () => {
    if (!evento) {
      return;
    }
    await evento.prompt();
    await evento.userChoice;
    setEvento(null);
    setOculto(true);
  }, [evento]);

  const dispensar = useCallback(() => {
    try {
      localStorage.setItem(CHAVE_DISPENSA, String(Date.now()));
    } catch {
      // sem localStorage: apenas oculta nesta sessão
    }
    setOculto(true);
  }, []);

  if (oculto || (!evento && !mostrarIOS)) {
    return null;
  }

  return (
    <aside className="m-install" aria-label="Instalar aplicativo CAINT">
      <img className="m-install-icon" src="/icon-192.png" alt="" width={44} height={44} />
      <div className="m-install-txt">
        <b>Instalar o app CAINT</b>
        <span>
          {evento
            ? "Acesse como aplicativo, em tela cheia e offline."
            : "Toque em Compartilhar e em “Adicionar à Tela de Início”."}
        </span>
      </div>
      {evento ? (
        <button type="button" className="m-btn primary m-install-cta" onClick={() => void instalar()}>
          Instalar
        </button>
      ) : (
        <span className="m-install-share" aria-hidden="true">
          {/* ícone "compartilhar" do iOS */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <title>Compartilhar</title>
            <path d="M12 3v13M12 3l-4 4M12 3l4 4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" strokeLinecap="round" />
          </svg>
        </span>
      )}
      <button type="button" className="m-install-x" onClick={dispensar} aria-label="Dispensar">
        ×
      </button>
    </aside>
  );
}
